import { ProfileProps } from '@design-system/global-header';
import {
  LogoDataProps,
  SkipLinkProps,
} from '@design-system/global-header/dist/types/models';
import { navigate } from 'gatsby';

import { ExtendedGlobalHeaderProps } from '~/components/ExtendedGlobalHeader';
import TMPLinkWrap from '~/components/TMPLinkWrap';
import { buttonMapper, buttonWithLinkMapper } from '~/mappers/button';
import { iconMapper } from '~/mappers/common';
import { linkMapper } from '~/mappers/link';

import { imageMapper } from '../image';
import {
  LAST_SEARCHES_QUANTITY,
  LOCAL_STORAGE_LAST_SEARCHES_KEY,
} from './constants';
import {
  ExtendedHeaderBasic,
  ExtendedHeaderCommon,
  Logo,
  Profile,
  SearchBlock,
  SearchBlockProps,
  SkipLink,
} from './models';

const headerLogoMapper = ({
  smallImage,
  largeImage,
  link,
  alt,
}: Logo): LogoDataProps => ({
  imageSmall: imageMapper({ image: smallImage, alt }, true)!,
  imageLarge: imageMapper({ image: largeImage, alt }, true)!,
  ...{ link: { component: TMPLinkWrap, props: linkMapper(link) } },
});

const skipLinkMapper = ({ mode, link }: SkipLink): SkipLinkProps => ({
  ...linkMapper(link),
  mode,
});

const searchMapper = (search: SearchBlock[]): SearchBlockProps | undefined => {
  if (!search[0]) return undefined;

  const [
    {
      searchLabel,
      searchInputPlaceholder,
      ariaLabelOpen,
      ariaLabelClose,
      swipeLabel,
      lastSearchesTitle,
      searchButtonText,
      searchResultsLink,
    },
  ] = search;

  return {
    searchLabel,
    searchInputPlaceholder,
    ariaLabelOpen,
    ariaLabelClose,
    swipeLabel,
    localStorageLastSearchesKey: LOCAL_STORAGE_LAST_SEARCHES_KEY,
    lastSearchesQuantity: LAST_SEARCHES_QUANTITY,
    lastSearchesTitle,
    searchButtonText,
    customNavigate: (searchText) => {
      navigate(`${searchResultsLink[0].url}${searchText}`);
    },
    searchPageUrl: `${searchResultsLink[0].url}`,
  };
};

const profileMapper = ({
  headline,
  greeting,
  icon,
  account,
  register,
  logIn,
  logOut,
}: Profile) => ({
  headline,
  greeting,
  icon: iconMapper(icon[0]),
  account: linkMapper(account),
  register: buttonWithLinkMapper(register),
  logIn: buttonWithLinkMapper(logIn),
  logOut: buttonMapper(logOut),
  linkNavElement: TMPLinkWrap,
});

export const headerCommonMapper = ({
  logo,
  burger: {
    openIcon: [burgerOpenIcon],
    closeIcon: [burgerCloseIcon],
    toggleLabel: burgerToggleLabel,
    closeLabel: burgerCloseLabel,
    openLabel: burgerOpenLabel,
  },
  headerSubtitle,
  navigationAriaLabel,
  skipLink,
  search,
  uid,
  profile,
}: ExtendedHeaderCommon) => ({
  logo: headerLogoMapper(logo),
  headerSubtitle,
  burgerOpenIcon: iconMapper(burgerOpenIcon),
  burgerCloseIcon: iconMapper(burgerCloseIcon),
  burgerToggleLabel,
  burgerCloseLabel,
  burgerOpenLabel,
  navigationName: navigationAriaLabel,
  skipLink: skipLinkMapper(skipLink),
  search: searchMapper(search),
  uid,
  profile: profile
    ? (profileMapper(profile) as unknown as ProfileProps)
    : undefined,
});

export const headerBasicMapper = ({
  navigation,
  ...rest
}: ExtendedHeaderBasic): ExtendedGlobalHeaderProps => ({
  ...headerCommonMapper(rest),
  navigationData: navigation,
});

export const extendedHeaderMapper = ({
  extendedHeaderBasic,
  profile,
}: {
  extendedHeaderBasic?: ExtendedHeaderBasic | null;
  profile?: Profile;
}) => {
  if (extendedHeaderBasic)
    return headerBasicMapper({ ...extendedHeaderBasic, profile });

  return null;
};
